import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Copy, PartyPopper, Plus, Trash2, Layers, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { celebrationService, type QuickCreateCelebrationResponse } from '@/lib/api/celebration.service';
import { handleApiError } from '@/lib/api/client';
import { clearCrossModeDraft } from '@/lib/celebrations/crossModeDraft';
import { buildQuickCreatePayload } from '@/lib/celebrations/quickCreatePayload';
import type { CelebrationFeatureProfile, CelebrantEntry } from '@om/shared-types';
import * as celebrantConfig from '@om/shared-types/celebrant-config';
const { ensureMinCelebrants } = celebrantConfig;
import { useAuth } from '@/hooks/useAuth';
import { getEmojiForType, getLabelForType, getSubtypesForType } from '@/config/celebrationRegistry';
import { SubtypePicker } from '@/components/celebrations/SubtypePicker';
import { CelebrantsEditor } from '@/components/celebrations/CelebrantsEditor';
import { getAbsoluteInviteUrl } from '@/utils/inviteUrl';
import {
  CelebrationGuestsCard,
  type GuestInviteMode,
  type LinkAccessType,
  type EmailDispatchMode,
} from '@/components/celebrations/CelebrationGuestsCard';

const todayDate = (): string => new Date().toISOString().slice(0, 10);

interface SubEventDraft {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  locationName: string;
}

const DEFAULT_FEATURE_PROFILE: CelebrationFeatureProfile = {
  quick_mode_enabled: true,
  module_flags: {
    venues: false,
    services: true,
    registry: false,
    budget: true,
    goals: true,
    guest_wall: false,
  },
  service_mode: 'order_food',
};

const VIBE_TAG_PRESETS = [
  { id: 'beach', label: '🌴 Beach & Coastal' },
  { id: 'rustic', label: '🌿 Rustic & Nature' },
  { id: 'black_tie', label: '🍷 Formal & Elegant' },
  { id: 'party', label: '🎉 Upbeat & Party' },
  { id: 'casual', label: '🍕 Casual & Fun' },
  { id: 'boho', label: '🎨 BoHo & Artistic' },
  { id: 'music_lovers', label: '🎶 Music Lovers' },
  { id: 'foodies', label: '🍕 Foodies' },
];

export const UnifiedCelebrationForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const typeParam = searchParams.get('type') || 'birthday';
  const isMultiParam = searchParams.get('multi') === 'true';

  // Core Form State
  const [title, setTitle] = useState('');
  const [celebrationType] = useState(typeParam);
  const [celebrationSubtype, setCelebrationSubtype] = useState<string | null>(null);
  const [isMultiEvent, setIsMultiEvent] = useState(isMultiParam);
  const [eventDate, setEventDate] = useState(todayDate());
  const [endDate, setEndDate] = useState(todayDate());
  const [startTime, setStartTime] = useState('19:00');
  const [endTime, setEndTime] = useState('22:00');
  const [locationName, setLocationName] = useState('');
  const [hostName, setHostName] = useState('');
  const [hostEmail, setHostEmail] = useState('');
  const [celebrants, setCelebrants] = useState<CelebrantEntry[]>(() => ensureMinCelebrants([{ name: '', role: '', email: '' }], typeParam));
  const [description, setDescription] = useState('');

  // Guest & Invitation State
  const [guestMode, setGuestMode] = useState<GuestInviteMode>('link');
  const [linkAccess, setLinkAccess] = useState<LinkAccessType>('open');
  const [rsvpDeadline, setRsvpDeadline] = useState('');
  const [allowPlusOnes, setAllowPlusOnes] = useState(false);
  const [plusOnesLimit, setPlusOnesLimit] = useState(1);
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const [emailDispatchMode, setEmailDispatchMode] = useState<EmailDispatchMode>('immediate');
  const [guestCountEstimate, setGuestCountEstimate] = useState<number | undefined>(undefined);
  const [trackDietary, setTrackDietary] = useState(true);
  const [seatingLayoutEnabled, setSeatingLayoutEnabled] = useState(false);
  const [groupTagsEnabled, setGroupTagsEnabled] = useState(false);

  // Vibe Tags State
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTagInput, setCustomTagInput] = useState('');

  // Multi-Event Sub-Events State
  const [subEvents, setSubEvents] = useState<SubEventDraft[]>([]);

  // Feature Profile / Module Toggles
  const [featureProfile] = useState<CelebrationFeatureProfile>(DEFAULT_FEATURE_PROFILE);

  // Submit & Share State
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<QuickCreateCelebrationResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Sync user defaults
  useEffect(() => {
    if (user?.email && !hostEmail) setHostEmail(user.email);
    if (user?.full_name && !hostName) setHostName(user.full_name);
    if (user?.full_name && !celebrants[0]?.name) {
      setCelebrants((prev) => prev.map((c, i) => (i === 0 ? { ...c, name: user.full_name! } : c)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle preset sub-events based on celebration type when toggling multi-event
  const handleToggleMultiEvent = () => {
    if (!isMultiEvent && subEvents.length === 0) {
      if (celebrationType === 'wedding') {
        setSubEvents([
          { id: '1', title: 'Rehearsal Dinner', date: eventDate, startTime: '18:00', endTime: '21:00', locationName: '' },
          { id: '2', title: 'Ceremony & Reception', date: eventDate, startTime: '16:00', endTime: '22:00', locationName: '' },
          { id: '3', title: 'Farewell Brunch', date: endDate, startTime: '10:00', endTime: '13:00', locationName: '' },
        ]);
      } else {
        setSubEvents([
          { id: '1', title: 'Main Event', date: eventDate, startTime: '18:00', endTime: '22:00', locationName: '' },
          { id: '2', title: 'After Party', date: eventDate, startTime: '22:00', endTime: '01:00', locationName: '' },
        ]);
      }
    }
    setIsMultiEvent(!isMultiEvent);
  };

  const addSubEvent = () => {
    setSubEvents((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        title: `Sub-Event ${prev.length + 1}`,
        date: eventDate,
        startTime: '18:00',
        endTime: '21:00',
        locationName: '',
      },
    ]);
  };

  const removeSubEvent = (id: string) => {
    setSubEvents((prev) => prev.filter((s) => s.id !== id));
  };

  const updateSubEvent = (id: string, field: keyof SubEventDraft, value: string) => {
    setSubEvents((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const addCustomTag = () => {
    if (!customTagInput.trim()) return;
    const tag = customTagInput.trim().toLowerCase().replace(/\s+/g, '_');
    if (!selectedTags.includes(tag)) {
      setSelectedTags((prev) => [...prev, tag]);
    }
    setCustomTagInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const built = buildQuickCreatePayload({
      title,
      celebrationType,
      celebrationSubtype,
      isMultiEvent,
      eventDate,
      endDate,
      startTime,
      endTime,
      locationName,
      hostName,
      hostEmail,
      celebrantName: celebrants[0]?.name ?? '',
      celebrantRole: celebrants[0]?.role,
      celebrantEmail: celebrants[0]?.email ?? '',
      additionalCelebrants: celebrants.slice(1),
      featureProfile,
      description,
      rsvpDeadline,
      maxGuests: guestCountEstimate,
      plusOnesAllowed: allowPlusOnes,
      plusOnesLimit: plusOnesLimit,
      accessType: guestMode === 'link' ? (linkAccess === 'registered' ? 'registered' : 'open') : 'invite_only',
      guestMode,
      guestEmails,
      emailDispatchMode,
      guestCountEstimate,
    });

    if (!built.ok) {
      setErrorMessage(built.error);
      return;
    }

    setSubmitting(true);
    try {
      const resp = await celebrationService.quickCreatePublicCelebration(built.payload);
      setResult(resp);
      clearCrossModeDraft();
    } catch (err) {
      setErrorMessage(handleApiError(err) || 'Failed to create celebration.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const shareUrl = getAbsoluteInviteUrl(result.share_url) || window.location.origin;
    const celebrationId = result.celebration?.id || '';

    return (
      <PageContainer width="default" className="space-y-4 py-6">
        <AppBreadcrumbs />
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <PartyPopper className="h-7 w-7 text-primary" />
              {title} is Ready!
            </CardTitle>
            <CardDescription>
              {guestMode === 'link' && (
                linkAccess === 'registered'
                  ? 'Your celebration has been created with verified registration access. Share your link with guests.'
                  : 'Your celebration has been created with open link RSVP. Anyone with the link can RSVP immediately!'
              )}
              {guestMode === 'email' && (
                `Your celebration has been created. ${guestEmails.length > 0 ? `${guestEmails.length} guest email${guestEmails.length === 1 ? '' : 's'} registered.` : 'Share your invite link or dispatch invitations below.'}`
              )}
              {guestMode === 'detailed' && (
                'Your celebration workspace and full guest coordination suite is initialized and ready.'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-white p-3 flex items-center justify-between gap-2">
              <p className="truncate text-sm text-primary font-medium">{shareUrl}</p>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); }}>
                <Copy className="h-3.5 w-3.5 mr-1" />
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>

            {guestMode === 'email' && guestEmails.length > 0 && (
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-xs text-purple-900 space-y-1">
                <p className="font-semibold">✉️ Guest Invitations Ready ({guestEmails.length} recipients):</p>
                <p className="text-purple-700">
                  {emailDispatchMode === 'immediate'
                    ? 'Invitations are queued to be dispatched immediately.'
                    : 'Invitations are saved as drafts in your workspace to review before sending.'}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate(`/celebrations/${celebrationId}`)}>
                Go to Celebration Workspace
              </Button>
              {celebrationId && (
                <Button variant="outline" onClick={() => navigate(`/celebrations/${celebrationId}/edit`)}>
                  Edit Celebration Details
                </Button>
              )}
              {guestMode === 'detailed' && celebrationId && (
                <Button variant="outline" onClick={() => navigate(`/celebrations/${celebrationId}/guests`)}>
                  Open Guest Management
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="default" className="space-y-6 py-6">
      <AppBreadcrumbs />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span>{getEmojiForType(celebrationType)}</span>
            Create {getLabelForType(celebrationType)}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isMultiEvent ? 'Multi-event celebration with sub-events and schedule' : 'Single-event quick celebration setup'}
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleToggleMultiEvent}
          className="gap-1.5"
        >
          <Layers className="h-4 w-4" />
          {isMultiEvent ? 'Switch to Quick Single-Event' : '+ Add Sub-Events (Multi-Event)'}
        </Button>
      </div>

      {getSubtypesForType(celebrationType).length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <SubtypePicker
              celebrationType={celebrationType}
              selectedSubtype={celebrationSubtype}
              onSelect={setCelebrationSubtype}
            />
          </CardContent>
        </Card>
      )}

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Core Event Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Event Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="event-title">Event Title *</Label>
              <Input
                id="event-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Sarah's 30th Birthday / Alex & Sam's Wedding Weekend"
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="event-date">{isMultiEvent ? 'Start Date' : 'Date'}</Label>
                <Input
                  id="event-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  required
                />
              </div>

              {isMultiEvent ? (
                <div>
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="start-time">Start Time</Label>
                    <Input
                      id="start-time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-time">End Time</Label>
                    <Input
                      id="end-time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="location">Location / Venue</Label>
              <LocationAutocomplete
                value={locationName}
                onLocationSelect={(loc) => setLocationName(loc.name)}
                placeholder="Venue name or address"
              />
            </div>

            <div>
              <Label htmlFor="event-description">Description & Notes (Optional)</Label>
              <Input
                id="event-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell guests about your celebration plans or dress code"
              />
            </div>
          </CardContent>
        </Card>

        {/* Host & Celebrant Contact */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Host & Celebrant</CardTitle>
            <CardDescription>We use these details to send the celebration link and invites.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="host-name">Your Name</Label>
                <Input
                  id="host-name"
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder="e.g. Alex Morgan"
                />
              </div>
              <div>
                <Label htmlFor="host-email">Your Email *</Label>
                <Input
                  id="host-email"
                  type="email"
                  value={hostEmail}
                  onChange={(e) => setHostEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Who is it for?</Label>
              <CelebrantsEditor
                celebrationType={celebrationType}
                celebrants={celebrants}
                onChange={setCelebrants}
                hostEmail={hostEmail}
                idPrefix="unified-celebrant"
              />
            </div>
          </CardContent>
        </Card>

        {/* Guests & Invitations Card */}
        <CelebrationGuestsCard
          mode={guestMode}
          onModeChange={setGuestMode}
          linkAccess={linkAccess}
          onLinkAccessChange={setLinkAccess}
          rsvpDeadline={rsvpDeadline}
          onRsvpDeadlineChange={setRsvpDeadline}
          allowPlusOnes={allowPlusOnes}
          onAllowPlusOnesChange={setAllowPlusOnes}
          plusOnesLimit={plusOnesLimit}
          onPlusOnesLimitChange={setPlusOnesLimit}
          guestEmails={guestEmails}
          onGuestEmailsChange={setGuestEmails}
          emailDispatchMode={emailDispatchMode}
          onEmailDispatchModeChange={setEmailDispatchMode}
          guestCountEstimate={guestCountEstimate}
          onGuestCountEstimateChange={setGuestCountEstimate}
          trackDietary={trackDietary}
          onTrackDietaryChange={setTrackDietary}
          seatingLayoutEnabled={seatingLayoutEnabled}
          onSeatingLayoutEnabledChange={setSeatingLayoutEnabled}
          groupTagsEnabled={groupTagsEnabled}
          onGroupTagsEnabledChange={setGroupTagsEnabled}
          eventSlugPreview={
            (title || celebrants[0]?.name || `${celebrationType}-party`)
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '') || `${celebrationType}-party`
          }
        />

        {/* Multi-Event Sub-Events Builder */}
        {isMultiEvent && (
          <Card className="border-primary/30">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Sub-Events & Timeline</CardTitle>
                <CardDescription>Add the schedule of events for your celebration weekend</CardDescription>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addSubEvent} className="gap-1">
                <Plus className="h-4 w-4" /> Add Sub-Event
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {subEvents.map((sub, index) => (
                <div key={sub.id} className="p-3 border rounded-lg bg-muted/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Event #{index + 1}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeSubEvent(sub.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Input
                      placeholder="Event Title"
                      value={sub.title}
                      onChange={(e) => updateSubEvent(sub.id, 'title', e.target.value)}
                    />
                    <Input
                      type="date"
                      value={sub.date}
                      onChange={(e) => updateSubEvent(sub.id, 'date', e.target.value)}
                    />
                    <div className="flex gap-1">
                      <Input
                        type="time"
                        value={sub.startTime}
                        onChange={(e) => updateSubEvent(sub.id, 'startTime', e.target.value)}
                      />
                      <Input
                        type="time"
                        value={sub.endTime}
                        onChange={(e) => updateSubEvent(sub.id, 'endTime', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Vibe & Style Tags */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Vibe & Style Tags</CardTitle>
                <CardDescription>Select tags or add your own to personalize your AI suggestions</CardDescription>
              </div>
              {selectedTags.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {selectedTags.length} selected
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Preset Tags */}
            <div className="flex flex-wrap gap-2">
              {VIBE_TAG_PRESETS.map((tag) => (
                <Badge
                  key={tag.id}
                  variant={selectedTags.includes(tag.id) ? 'default' : 'outline'}
                  className="cursor-pointer text-xs py-1 px-2.5 transition-all select-none hover:opacity-80"
                  onClick={() => toggleTag(tag.id)}
                >
                  {tag.label}
                </Badge>
              ))}
            </div>

            {/* Custom Tags List */}
            {selectedTags.some((t) => !VIBE_TAG_PRESETS.some((p) => p.id === t)) && (
              <div className="space-y-1.5 pt-2 border-t">
                <span className="text-xs font-semibold text-muted-foreground block">Custom Tags:</span>
                <div className="flex flex-wrap gap-2">
                  {selectedTags
                    .filter((t) => !VIBE_TAG_PRESETS.some((p) => p.id === t))
                    .map((customTag) => (
                      <Badge
                        key={customTag}
                        variant="secondary"
                        className="text-xs py-1 pl-2.5 pr-1.5 flex items-center gap-1.5 bg-primary/10 text-primary border-primary/20"
                      >
                        #{customTag.replace(/_/g, ' ')}
                        <button
                          type="button"
                          className="rounded-full p-0.5 hover:bg-primary/20 hover:text-red-600 transition-colors"
                          onClick={() => toggleTag(customTag)}
                          aria-label={`Remove tag ${customTag}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                </div>
              </div>
            )}

            {/* Add Custom Tag Input */}
            <div className="flex gap-2 max-w-sm pt-1">
              <Input
                placeholder="Add custom tag (e.g. superhero, retro)..."
                value={customTagInput}
                onChange={(e) => setCustomTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTag();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addCustomTag}>
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/celebrations')}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={submitting}>
            {submitting ? 'Creating Celebration...' : '🚀 Launch Celebration Workspace'}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
};
